import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import logoWithTextImg from "~/assets/logo-with-text.svg";
import Announcement from "~/components/common/Announcement";
import { useUserStore } from "~/lib/stores/user";
import { Search } from "lucide-react";
import { BlurryBackground } from "~/components/common/BlurryBackground";
import { IoPersonOutline } from "react-icons/io5";
import Login from "~/components/common/Login";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import GetApp from "./GetApp";
import { isGuestUser } from "~/lib/user";
import SearchInput from "~/pages/deal/search/input";
import { cn } from "~/lib/utils";
export interface HeaderProps {
  showAnnouncement?: boolean;
  showCategoriesMenu?: boolean;
  showBlidzAppMenu?: boolean;
  showSearchBar?: boolean;
  showAuthControl?: boolean;
}

export default function Header({
  showAnnouncement = true,
  showCategoriesMenu = false,
  showBlidzAppMenu = false,
  showSearchBar = true,
  showAuthControl = true,
}: HeaderProps) {
  const user = useUserStore(state => state.user);
  const isLoggedIn = !!user && !isGuestUser(user);
  const [loginOpen, setLoginOpen] = useState(false);
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <BlurryBackground
      position="fixed"
      className="top-0 left-0 z-50 w-screen bg-gradient-to-b from-[rgba(255,251,205,0.5)] to-[rgba(255,255,255,0.5)]"
    >
      {showAnnouncement && <Announcement />}
      <div id="header-content" className="container mx-auto flex h-16 items-center justify-between px-4">
        <div id="header-left-section" className="flex items-center gap-4">
          <Link id="header-logo-link" to="/shop">
            <img id="header-logo" src={logoWithTextImg} alt="Blidz" className="h-8" />
          </Link>
          <nav id="header-nav" className="hidden items-center gap-6 md:flex">
            {showCategoriesMenu && (
              <a id="header-nav-categories" href="#" className="text-sm font-medium text-gray-700 hover:text-black">
                Categories
              </a>
            )}
            {showBlidzAppMenu && (
              <>
                <button
                  id="header-button-blidz-app"
                  onClick={() => setGetAppOpen(true)}
                  className="text-sm font-medium text-gray-700 hover:text-black"
                >
                  Blidz App
                </button>
                <GetApp open={getAppOpen} onOpenChange={setGetAppOpen} />
              </>
            )}
          </nav>
        </div>

        {showSearchBar && (
          <div id="header-search-container" className="mx-4 max-w-[516px] flex-1">
            <div id="header-search-wrapper" className="relative">
              <Search
                id="header-search-icon"
                className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
              />
              <input
                id="header-search-input"
                onClick={() => setSearchDialogOpen(true)}
                type="text"
                placeholder="Search"
                className={cn(
                  "w-full rounded-[24px] bg-[#0000000D]",
                  "py-2 pr-4 pl-10",
                  "focus:border-none focus:shadow-none focus:outline-none",
                  searchDialogOpen && "bg-opacity-0"
                )}
              />
              {searchDialogOpen && (
                <SearchInput
                  onSearch={keyword => {
                    setSearchDialogOpen(false);
                    navigate(`/shop/search?keyword=${encodeURIComponent(keyword)}`);
                  }}
                  onClose={() => setSearchDialogOpen(false)}
                />
              )}
            </div>
          </div>
        )}

        <div id="header-right-section" className="flex cursor-pointer items-center gap-4">
          {showAuthControl && (
            <>
              {isLoggedIn && user ? (
                <Avatar
                  id="header-avatar"
                  onClick={() => navigate("/user/profile")}
                  className="border border-[#0000000D]"
                >
                  <AvatarImage id="header-avatar-image" src="" alt={user.basicInfo?.name || "User"} />
                  <AvatarFallback id="header-avatar-fallback">
                    {user.basicInfo?.name?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div id="header-auth-container" className="flex items-center">
                  <Button id="header-button-sign-in" onClick={() => setLoginOpen(true)} className="rounded-[12px]">
                    Sign in
                    <IoPersonOutline />
                  </Button>
                  <Login open={loginOpen} onOpenChange={setLoginOpen} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showSearchBar && searchDialogOpen && (
        <div
          id="header-search-mask"
          className="fixed top-0 left-0 z-51 min-h-screen min-w-screen bg-black/35"
          onClick={() => setSearchDialogOpen(false)}
        />
      )}
    </BlurryBackground>
  );
}
